/**
 * API client for scheduling operations.
 * Handles booking types, bookings, and availability.
 */

import { apiGet, apiPatch, apiPost } from '@/lib/http/client';

import type {
  AvailabilityOptions,
  AvailabilityResponse,
  BookingType,
  CancelBookingRequest,
  CancelBookingResponse,
  CreateBookingRequest,
  CreateBookingResponse,
  PublicBookingType,
  RescheduleBookingRequest,
  RescheduleDetailsResponse,
  UpdateBookingTypeRequest,
} from './types/scheduling';

// ============================================================================
// Internal API (Authenticated, Org-Scoped)
// ============================================================================

/**
 * Update a booking type.
 */
export async function updateBookingType(
  orgId: number,
  entityId: string,
  data: UpdateBookingTypeRequest
): Promise<BookingType> {
  return apiPatch<BookingType>(
    `/api/orgs/${orgId}/scheduling/booking-types/${entityId}/`,
    data
  );
}

/**
 * Cancel a booking by entity ID (as the host).
 */
export async function cancelBookingAsHost(
  orgId: number,
  entityId: string,
  reason?: string
): Promise<{ message: string }> {
  return apiPost<{ message: string }>(
    `/api/orgs/${orgId}/scheduling/bookings/${entityId}/`,
    { action: 'cancel', reason }
  );
}

// ============================================================================
// Public API (No Authentication Required)
// ============================================================================

/**
 * Get a public booking type by username and slug.
 */
export async function getPublicBookingType(
  username: string,
  slug: string
): Promise<PublicBookingType> {
  return apiGet<PublicBookingType>(
    `/api/public/scheduling/schedule/${username}/${slug}/`
  );
}

/**
 * Get a public booking type by short code.
 */
export async function getPublicBookingTypeByCode(
  shortCode: string
): Promise<PublicBookingType> {
  return apiGet<PublicBookingType>(`/api/public/scheduling/m/${shortCode}/`);
}

/**
 * Get a public booking type by global slug.
 */
export async function getPublicBookingTypeByGlobalSlug(
  globalSlug: string
): Promise<PublicBookingType> {
  return apiGet<PublicBookingType>(`/api/public/scheduling/g/${globalSlug}/`);
}

/**
 * Get available time slots for a meeting type (by username/slug).
 */
export async function getAvailability(
  username: string,
  slug: string,
  options: AvailabilityOptions = {}
): Promise<AvailabilityResponse> {
  return apiGet<AvailabilityResponse>(
    `/api/public/scheduling/schedule/${username}/${slug}/availability/`,
    { ...options }
  );
}

/**
 * Get available time slots for a meeting type (by short code).
 */
export async function getAvailabilityByCode(
  shortCode: string,
  options: AvailabilityOptions = {}
): Promise<AvailabilityResponse> {
  return apiGet<AvailabilityResponse>(
    `/api/public/scheduling/m/${shortCode}/availability/`,
    { ...options }
  );
}

/**
 * Create a booking (by username/slug).
 */
export async function createBooking(
  username: string,
  slug: string,
  data: CreateBookingRequest
): Promise<CreateBookingResponse> {
  return apiPost<CreateBookingResponse>(
    `/api/public/scheduling/schedule/${username}/${slug}/book/`,
    data
  );
}

/**
 * Create a booking (by short code).
 */
export async function createBookingByCode(
  shortCode: string,
  data: CreateBookingRequest
): Promise<CreateBookingResponse> {
  return apiPost<CreateBookingResponse>(
    `/api/public/scheduling/m/${shortCode}/book/`,
    data
  );
}

/**
 * Create a booking (by global slug).
 */
export async function createBookingByGlobalSlug(
  globalSlug: string,
  data: CreateBookingRequest
): Promise<CreateBookingResponse> {
  return apiPost<CreateBookingResponse>(
    `/api/public/scheduling/g/${globalSlug}/book/`,
    data
  );
}

/**
 * Cancel a booking using the cancellation token.
 */
export async function cancelBooking(
  data: CancelBookingRequest
): Promise<CancelBookingResponse> {
  return apiPost<CancelBookingResponse>(
    '/api/public/scheduling/bookings/cancel/',
    data
  );
}

/**
 * Get booking details and availability for rescheduling.
 */
export async function getRescheduleDetails(
  cancelToken: string,
  options: { timezone?: string } = {}
): Promise<RescheduleDetailsResponse> {
  return apiGet<RescheduleDetailsResponse>(
    `/api/public/scheduling/bookings/reschedule/${cancelToken}/`,
    options
  );
}

/**
 * Reschedule a booking to a new time.
 */
export async function rescheduleBooking(
  cancelToken: string,
  data: RescheduleBookingRequest
): Promise<CreateBookingResponse> {
  return apiPost<CreateBookingResponse>(
    `/api/public/scheduling/bookings/reschedule/${cancelToken}/`,
    data
  );
}

// ============================================================================
// Aliases (for backward compatibility)
// ============================================================================

/**
 * Alias for createBooking.
 */
export const bookMeeting = createBooking;

/**
 * Alias for createBookingByCode.
 */
export const bookMeetingByCode = createBookingByCode;

/**
 * Alias for createBookingByGlobalSlug.
 */
export const bookMeetingByGlobalSlug = createBookingByGlobalSlug;
